import useSWRImmutable from 'swr/immutable';
import { type GameV1, gamesFilterSelectBySEOTitle } from '@/api/games';
import { CLIENT_ENVS } from '@/helpers/constants';
import { CurrencyCode } from '@/helpers/currency';
import { sendErrorHotAndColdEvent } from '@/modules/googleAnalytics/events/gamesEvents';
import { convertStatisticGames, handleErrorHotAndColdEvent } from '../helpers';
import { useUserCurrency } from './useUserCurrency';

interface IHotAndColdGameResponse {
  success: boolean;
  data: IHotAndColdGameData;
  error: unknown;
}
export interface IHotAndColdGameData {
  hot_games: IHotAndColdGameBase[];
  cold_games: IHotAndColdGameBase[];
  generated_at: string;
}
export interface IHotAndColdGameBase {
  title: string;
  demo: boolean;
  seo_title: string;
  payout: string;
  rank: number;
  identifier: string;
  provider: string;
}
export interface IHotGame extends IHotAndColdGameBase {
  hot: boolean;
}
export interface IColdGame extends IHotAndColdGameBase {
  cold: boolean;
}
const HOT_AND_COLD_GAMES_LIMIT = 30;

const MAP_CURRENCY_TO_MIN_PLAYERS: Record<string, number> = {
  [CurrencyCode.AUD]: 3,
  [CurrencyCode.EUR]: 2,
  [CurrencyCode.CAD]: 1,
};

export const fetchHotAndColdGames = async (
  currency: CurrencyCode | null
): Promise<{ hotGames: GameV1[]; coldGames: GameV1[] }> => {
  if (!currency) return { hotGames: [], coldGames: [] };

  try {
    const minPlayers = MAP_CURRENCY_TO_MIN_PLAYERS[currency];
    const response = await fetch(
      `${CLIENT_ENVS.BQ_API_URL}/api/v1/hot-and-cold?currency=${currency}&limit=${HOT_AND_COLD_GAMES_LIMIT}&min_players=${minPlayers}`
    );

    if (!response.ok) {
      sendErrorHotAndColdEvent({ type: 'server_error' });
      return { hotGames: [], coldGames: [] };
    }

    const { data } = (await response.json()) as IHotAndColdGameResponse;

    const hotGames: IHotGame[] = data.hot_games.map((g) => ({
      ...g,
      hot: true,
    }));
    const coldGames: IColdGame[] = data.cold_games.map((g) => ({
      ...g,
      cold: true,
    }));

    const mergedGames = [...hotGames, ...coldGames];
    const seoTitles = mergedGames.map(({ seo_title }) => seo_title);
    const { data: filteredGamesById = {} } = await gamesFilterSelectBySEOTitle(
      { gameSeoTitles: seoTitles },
      (data) => data
    );

    const filteredHotGames = hotGames.filter(
      (game) => filteredGamesById[game.identifier]
    );
    const filteredColdGames = coldGames.filter(
      (game) => filteredGamesById[game.identifier]
    );

    const noHotGames = !filteredHotGames.length;
    const noColdGames = !filteredColdGames.length;

    handleErrorHotAndColdEvent(noHotGames, noColdGames);

    return {
      hotGames: convertStatisticGames(filteredHotGames),
      coldGames: convertStatisticGames(filteredColdGames),
    };
  } catch (e) {
    console.warn(e);
    sendErrorHotAndColdEvent({ type: 'server_error' });
    return { hotGames: [], coldGames: [] };
  }
};

export const useHotAndColdGames = (disabled?: boolean) => {
  const { currency, isLoading: isCurrencyLoading } = useUserCurrency();

  const { data, error, isLoading } = useSWRImmutable(
    isCurrencyLoading || disabled ? null : `hot-and-cold-${currency}`,
    () => fetchHotAndColdGames(currency)
  );

  return {
    hotGames: data?.hotGames ?? [],
    coldGames: data?.coldGames ?? [],
    isLoading: isLoading || isCurrencyLoading,
    error,
  };
};
